// Bot Catalog — static catalog of this tenant's AI Agent build files.
//
// Powered by data/botCatalog.json which is generated out-of-band from the
// exported AI Agent Studio JSONs. For Prisma this is a snapshot of their
// 7 production bots as of 2026-10-22.
//
// Shows per-bot: type (autonomous vs scripted), tool / intent surface,
// declared custom-event exits, and (for autonomous bots) a prompt excerpt.
// Click a row for a detail drawer with the full catalog entry.
//
// Future enhancement path: swap the static JSON import for a backend
// `/ccutils/wxcc-bot-catalog` endpoint so bot-owners can upload-and-replace
// versions without rebuilding the SPA (Tier 3 roadmap).

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Code,
  Drawer,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconFileCode, IconInfoCircle, IconMessageCircle, IconRobot, IconTools } from '@tabler/icons-react';

import catalogData from './data/botCatalog.json';
import { CsvExportButton } from './components/CsvExport';

export function BotCatalog() {
  const bots = useMemo(() => Object.values(catalogData.bots ?? {}), []);
  const [selected, setSelected] = useState(null);

  const csvRows = useMemo(() => bots.map(toCsvRow), [bots]);

  return (
    <Stack>
      <div>
        <Title order={3}>Bot Catalog</Title>
        <Text size="sm" c="dimmed">
          Static catalog of this tenant's AI Agent build files. Click a bot for its full spec
          (tools / intents / declared custom-event exits / prompt excerpt). Generated{' '}
          {catalogData.generatedAt ?? '(unknown)'}. Source:{' '}
          {catalogData.source ?? '(unknown)'}.
        </Text>
      </div>

      <Alert color="gray" variant="light" icon={<IconInfoCircle size={14} />}>
        This is the Tier 1 static catalog &mdash; a snapshot from exported bot JSONs. Future iteration:
        upload / replace per bot without a rebuild. See the Spec vs Reality report under Analytics
        &rarr; Technical for the catalog-vs-observed-handoffs drift view.
      </Alert>

      <Paper withBorder p="md" radius="md">
        <Group justify="space-between" mb="xs">
          <div>
            <Title order={5}>{bots.length} bot{bots.length === 1 ? '' : 's'} in catalog</Title>
            <Text size="xs" c="dimmed">
              Click a row for the full spec drawer.
            </Text>
          </div>
          <CsvExportButton rows={csvRows} filename={`bot-catalog-${new Date().toISOString().slice(0, 10)}.csv`} />
        </Group>

        <ScrollArea>
          <Table verticalSpacing="xs" highlightOnHover striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Bot name</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Tools / Intents</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Prompt chars</Table.Th>
                <Table.Th>Declared custom-event exits</Table.Th>
                <Table.Th>Tool / Intent names</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {bots.map((b) => (
                <Table.Tr
                  key={b.bot_name}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelected(b)}
                >
                  <Table.Td>
                    <Group gap={6}>
                      <IconRobot size={14} />
                      <Text size="sm" fw={500}>{b.bot_name}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <TypeBadge type={b.bot_type} cisco={b.cisco_bot_type} />
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Text size="sm">
                      {b.bot_type === 'autonomous' ? b.tool_count : b.intent_count}
                    </Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Text size="xs" c="dimmed">{b.prompt_chars ?? '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    {(b.declared_custom_events ?? []).length === 0 ? (
                      <Text size="xs" c="dimmed">—</Text>
                    ) : (
                      <Group gap={4} wrap="wrap">
                        {b.declared_custom_events.map((e) => (
                          <Badge key={e} size="xs" variant="light" color="cyan">{e}</Badge>
                        ))}
                      </Group>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {b.bot_type === 'autonomous'
                        ? (b.tools ?? []).map((t) => t.name).join(', ')
                        : (b.intents ?? []).map((i) => i.name).join(', ')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      <BotDetailDrawer bot={selected} onClose={() => setSelected(null)} />
    </Stack>
  );
}

function TypeBadge({ type, cisco }) {
  const color = type === 'autonomous' ? 'blue' : 'violet';
  return (
    <Tooltip label={`Cisco bot_type: ${cisco}`}>
      <Badge size="sm" color={color} variant="light">
        {type}
      </Badge>
    </Tooltip>
  );
}

function BotDetailDrawer({ bot, onClose }) {
  if (!bot) return null;
  return (
    <Drawer
      opened={!!bot}
      onClose={onClose}
      position="right"
      size="xl"
      title={
        <Group gap="xs" align="baseline">
          <IconRobot size={16} />
          <Text fw={600}>{bot.bot_name}</Text>
          <TypeBadge type={bot.bot_type} cisco={bot.cisco_bot_type} />
        </Group>
      }
    >
      <Tabs defaultValue="summary">
        <Tabs.List>
          <Tabs.Tab value="summary" leftSection={<IconInfoCircle size={14} />}>Summary</Tabs.Tab>
          {bot.bot_type === 'autonomous' ? (
            <>
              <Tabs.Tab value="tools" leftSection={<IconTools size={14} />}>Tools ({bot.tool_count})</Tabs.Tab>
              <Tabs.Tab value="prompt" leftSection={<IconFileCode size={14} />}>Prompt</Tabs.Tab>
            </>
          ) : (
            <>
              <Tabs.Tab value="intents" leftSection={<IconMessageCircle size={14} />}>Intents ({bot.intent_count})</Tabs.Tab>
              <Tabs.Tab value="entities" leftSection={<IconFileCode size={14} />}>Entities ({bot.entity_count})</Tabs.Tab>
            </>
          )}
        </Tabs.List>

        <Tabs.Panel value="summary" pt="md">
          <Stack gap="xs">
            <KV label="Bot name" value={bot.bot_name} mono />
            <KV label="Source file" value={bot.source_file} mono />
            <KV label="Type" value={`${bot.bot_type} (Cisco: ${bot.cisco_bot_type})`} />
            {bot.welcome_message && <KV label="Welcome message" value={bot.welcome_message} />}
            {bot.first_message && <KV label="First message" value={bot.first_message} />}
            {bot.description && !bot.welcome_message && <KV label="Description" value={bot.description} />}
            {bot.languages && <KV label="Languages" value={(bot.languages || []).join(', ')} />}
            {bot.allow_agent_handover !== undefined && (
              <KV label="Agent handover allowed" value={bot.allow_agent_handover ? 'yes' : 'no'} />
            )}
            {bot.declared_custom_events && bot.declared_custom_events.length > 0 && (
              <div>
                <Text size="xs" c="dimmed">Declared custom-event exits</Text>
                <Group gap={4} wrap="wrap">
                  {bot.declared_custom_events.map((e) => (
                    <Badge key={e} size="sm" color="cyan" variant="light">{e}</Badge>
                  ))}
                </Group>
              </div>
            )}
          </Stack>
        </Tabs.Panel>

        {bot.bot_type === 'autonomous' && (
          <Tabs.Panel value="tools" pt="md">
            <Table verticalSpacing="xs" striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Fulfillment</Table.Th>
                  <Table.Th>Required params</Table.Th>
                  <Table.Th>Description</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(bot.tools ?? []).map((t) => (
                  <Table.Tr key={t.name}>
                    <Table.Td>
                      <Group gap={4}>
                        <Text size="sm" fw={500}>{t.name}</Text>
                        {t.system_tool && <Badge size="xs" color="gray">system</Badge>}
                        {bot.inferred_event_emitter_tools?.includes(t.name) && (
                          <Badge size="xs" color="cyan" variant="light">custom event</Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {t.fulfillment_type ?? '—'}
                        {t.flow_name ? ` · ${t.flow_name}` : ''}
                        {t.flow_id ? ` (${t.flow_id})` : ''}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>
                        {(t.required_params ?? []).join(', ') || '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td style={{ maxWidth: 400 }}>
                      <Text size="xs" c="dimmed" lineClamp={3}>{t.description || '—'}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Tabs.Panel>
        )}

        {bot.bot_type === 'autonomous' && (
          <Tabs.Panel value="prompt" pt="md">
            <Text size="xs" c="dimmed" mb="xs">
              {bot.prompt_chars} characters total. First 400 shown; full prompt lives in the source bot JSON.
            </Text>
            <Code block style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
              {bot.prompt_excerpt || '(no prompt)'}
            </Code>
          </Tabs.Panel>
        )}

        {bot.bot_type === 'scripted' && (
          <Tabs.Panel value="intents" pt="md">
            <Table verticalSpacing="xs" striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Entities</Table.Th>
                  <Table.Th>Exit context</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Variants</Table.Th>
                  <Table.Th>End conv?</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(bot.intents ?? []).map((i) => (
                  <Table.Tr key={i.name}>
                    <Table.Td><Text size="sm" fw={500}>{i.name}</Text></Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">{(i.entities ?? []).join(', ') || '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      {i.exit_context
                        ? <Badge size="xs" color="cyan" variant="light">{i.exit_context}</Badge>
                        : <Text size="xs" c="dimmed">—</Text>}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text size="xs">{i.variants_count}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{i.end_conversation ? 'yes' : 'no'}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Tabs.Panel>
        )}

        {bot.bot_type === 'scripted' && (
          <Tabs.Panel value="entities" pt="md">
            {(bot.entities ?? []).length === 0 ? (
              <Text size="sm" c="dimmed">No entities declared.</Text>
            ) : (
              <Table verticalSpacing="xs" striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Description</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {bot.entities.map((e) => (
                    <Table.Tr key={e.name}>
                      <Table.Td><Text size="sm" fw={500}>{e.name}</Text></Table.Td>
                      <Table.Td><Text size="xs" c="dimmed">{e.type ?? '—'}</Text></Table.Td>
                      <Table.Td><Text size="xs" c="dimmed">{e.description || '—'}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Tabs.Panel>
        )}
      </Tabs>
    </Drawer>
  );
}

function KV({ label, value, mono }) {
  return (
    <div>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm" style={mono ? { fontFamily: 'monospace' } : undefined}>{value}</Text>
    </div>
  );
}

function toCsvRow(b) {
  return {
    bot_name: b.bot_name,
    bot_type: b.bot_type,
    cisco_bot_type: b.cisco_bot_type,
    prompt_chars: b.prompt_chars ?? 0,
    tool_or_intent_count: b.bot_type === 'autonomous' ? (b.tool_count ?? 0) : (b.intent_count ?? 0),
    tool_or_intent_names: (b.bot_type === 'autonomous'
      ? (b.tools ?? []).map((t) => t.name)
      : (b.intents ?? []).map((i) => i.name)
    ).join('; '),
    declared_custom_events: (b.declared_custom_events ?? []).join('; '),
    event_emitter_tools: (b.inferred_event_emitter_tools ?? []).join('; '),
    source_file: b.source_file ?? '',
  };
}
